const modalSelector = '.as-stl-modal';
const focusableSelector = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

const modalOpeners = new WeakMap();
let activeModal = null;

function getFocusableElements(modal) {
  return Array.from(modal.querySelectorAll(focusableSelector)).filter(
    (element) => !element.hasAttribute('inert') && element.getClientRects().length > 0,
  );
}

function restoreModalFocus(modal) {
  const opener = modalOpeners.get(modal);
  if (opener instanceof HTMLElement && opener.isConnected) {
    opener.focus();
  }
  modalOpeners.delete(modal);
}

function syncModalState(modal, moveFocus = true) {
  const isOpen = modal.classList.contains('is-open');
  modal.setAttribute('aria-hidden', isOpen ? 'false' : 'true');
  modal.toggleAttribute('inert', !isOpen);

  if (isOpen) {
    activeModal = modal;
    if (moveFocus) {
      requestAnimationFrame(() => {
        const target =
          modal.querySelector('[data-close-modal], [data-stl-close]') ||
          modal.querySelector('[aria-labelledby]') ||
          modal;
        target.focus?.();
      });
    }
  } else if (activeModal === modal) {
    activeModal = null;
    restoreModalFocus(modal);
  }
}

function setupModal(modal) {
  if (modal.dataset.accessibleModalInitialized === 'true') return;

  modal.dataset.accessibleModalInitialized = 'true';
  modal.setAttribute('role', 'dialog');
  modal.setAttribute('aria-modal', 'true');

  if (!modal.hasAttribute('tabindex')) {
    modal.setAttribute('tabindex', '-1');
  }

  new MutationObserver(() => syncModalState(modal)).observe(modal, {
    attributes: true,
    attributeFilter: ['class'],
  });

  syncModalState(modal, modal.classList.contains('is-open'));
}

function setupModals(root = document) {
  root.querySelectorAll?.(modalSelector).forEach(setupModal);
}

document.addEventListener('click', (event) => {
  const trigger = event.target.closest('a[href^="#"][aria-controls]');
  if (!trigger) return;

  const modalId = trigger.getAttribute('aria-controls');
  const modal = modalId ? document.getElementById(modalId) : null;
  if (modal?.matches(modalSelector)) {
    modalOpeners.set(modal, trigger);
  }
});

document.addEventListener('keydown', (event) => {
  if (!activeModal) return;

  if (event.key === 'Escape') {
    event.preventDefault();
    const modal = activeModal;
    history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);
    modal.classList.remove('is-open');
    document.body.style.overflow = '';
    syncModalState(modal);
    return;
  }

  if (event.key !== 'Tab') return;

  const focusableElements = getFocusableElements(activeModal);
  if (focusableElements.length === 0) {
    event.preventDefault();
    activeModal.focus();
    return;
  }

  const first = focusableElements[0];
  const last = focusableElements[focusableElements.length - 1];

  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
});

new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    for (const node of mutation.addedNodes) {
      if (!(node instanceof Element)) continue;
      if (node.matches(modalSelector)) setupModal(node);
      setupModals(node);
    }
  }
}).observe(document.documentElement, { childList: true, subtree: true });

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => setupModals(), { once: true });
} else {
  setupModals();
}
